import { SelectField } from "@/components/SelectField";
import { GAS_PUMP_TYPES } from "@/lib/pdiOptions";
import {
  POS_PLATFORM_OPTIONS,
  POS_PLATFORM_OTHER,
} from "@/lib/posPlatforms";
import { pdiOrderWanted, type PdiPrefsInput } from "@/lib/types";
import type { FieldSetter } from "./types";
import { TF } from "./TF";

export function PdiFields({
  pdiPrefs,
  updatePdi,
  posOtherMode,
  setPosOtherSelected,
  gasOtherSelected,
  setGasOtherSelected,
  enrollmentOnly = false,
}: {
  pdiPrefs: PdiPrefsInput;
  updatePdi: FieldSetter<PdiPrefsInput>;
  posOtherMode: boolean;
  setPosOtherSelected: (v: boolean) => void;
  gasOtherSelected: boolean;
  setGasOtherSelected: (v: boolean) => void;
  // MNSP-enrollment-only path: the PDI order is opt-IN, so the toggle
  // defaults to "No" until the operator explicitly turns it on. On the
  // bundled path the order is the norm, so the (unset) default is "Yes".
  enrollmentOnly?: boolean;
}) {
  // Resolve the toggle's displayed value -- see pdiOrderWanted.
  const sendPdiOrder = pdiOrderWanted(pdiPrefs.sendPdiOrder, enrollmentOnly);
  const sendOrderToggle = (
    <div>
      <label htmlFor="pdi-send-order" className="label mb-1 block">
        Send a PDI managed-network order?
      </label>
      <select
        id="pdi-send-order"
        className="input"
        value={sendPdiOrder ? "Yes" : "No"}
        onChange={(e) => updatePdi("sendPdiOrder", e.target.value)}
      >
        <option value="Yes">
          Yes — email the order to PDI when the merchant signs
        </option>
        <option value="No">
          No — do not send a PDI order for this merchant
        </option>
      </select>
      <p className="mt-1 text-xs text-brand-muted">
        {enrollmentOnly
          ? "Enrollment-only applications don’t order equipment by default. Set to “Yes” to send a PDI order when the enrollment is signed."
          : "When set to “No”, no order is ever sent to PDI for this application."}
      </p>
    </div>
  );
  // No order -> the rest of the PDI inputs are irrelevant; show only the toggle.
  if (!sendPdiOrder) return sendOrderToggle;
  return (
    <>
      {sendOrderToggle}
      <div>
        <label
          htmlFor="ife-pdi-pos"
          className="mb-1 block text-xs text-brand-muted"
        >
          POS platform
        </label>
        <SelectField
          id="ife-pdi-pos"
          value={
            posOtherMode ? POS_PLATFORM_OTHER : (pdiPrefs.posPlatform ?? "")
          }
          options={[
            ...POS_PLATFORM_OPTIONS,
            { value: POS_PLATFORM_OTHER, label: "Other (not listed)" },
          ]}
          onChange={(v) => {
            if (v === POS_PLATFORM_OTHER) {
              // Switch to free-text mode; clear any known value so
              // the recipient types the actual platform.
              setPosOtherSelected(true);
              updatePdi("posPlatform", "");
            } else {
              setPosOtherSelected(false);
              updatePdi("posPlatform", v);
            }
          }}
          selectedLabel={posOtherMode ? "Other (not listed)" : undefined}
        />
        {posOtherMode && (
          <div className="mt-2">
            <TF
              id="pdi-pos-other"
              label="Enter POS platform"
              value={pdiPrefs.posPlatform ?? ""}
              onChange={(v) => updatePdi("posPlatform", v)}
              maxLength={200}
              placeholder="e.g., a cloud or regional POS not listed above"
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="pdi-sites" className="label mb-1 block">
            Site count
          </label>
          <select
            id="pdi-sites"
            className="input"
            value={pdiPrefs.singleOrMultiSite ?? "Single"}
            onChange={(e) => updatePdi("singleOrMultiSite", e.target.value)}
          >
            <option value="Single">Single site</option>
            <option value="Multi">Multi-site</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor="pdi-gas" className="label mb-1 block">
            Gas pumps?
          </label>
          <select
            id="pdi-gas"
            className="input"
            value={pdiPrefs.gasPumpYn ?? "No"}
            onChange={(e) => updatePdi("gasPumpYn", e.target.value)}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </div>
        <div>
          <label htmlFor="pdi-broadband" className="label mb-1 block">
            New broadband?
          </label>
          <select
            id="pdi-broadband"
            className="input"
            value={pdiPrefs.broadbandYn ?? "No"}
            onChange={(e) => updatePdi("broadbandYn", e.target.value)}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </div>
        <div>
          <label htmlFor="pdi-residential" className="label mb-1 block">
            Residential?
          </label>
          <select
            id="pdi-residential"
            className="input"
            value={pdiPrefs.residentialLocation ?? "No"}
            onChange={(e) => updatePdi("residentialLocation", e.target.value)}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </div>
      </div>
      {pdiPrefs.gasPumpYn === "Yes" && (
        <div>
          <label
            htmlFor="pdi-gas-type"
            className="mb-1 block text-xs text-brand-muted"
          >
            Gas pump type
          </label>
          {/* Dropdown per the 2026-07-13 meeting (issue #66),
              mirroring the merchant intake form (issue #87). Other
              keeps the free-text escape hatch; a stored value outside
              the known three hydrates into Other mode automatically. */}
          <select
            id="pdi-gas-type"
            className="block h-12 w-full rounded-md border border-brand-border bg-brand-bg px-3 text-base text-brand-text focus:border-brand-blue focus:outline-none"
            value={
              gasOtherSelected ||
              (!!pdiPrefs.gasPumpType &&
                !GAS_PUMP_TYPES.includes(pdiPrefs.gasPumpType))
                ? "__other__"
                : (pdiPrefs.gasPumpType ?? "")
            }
            onChange={(e) => {
              if (e.target.value === "__other__") {
                setGasOtherSelected(true);
                updatePdi("gasPumpType", "");
              } else {
                setGasOtherSelected(false);
                updatePdi("gasPumpType", e.target.value);
              }
            }}
          >
            <option value="">Select pump type</option>
            {GAS_PUMP_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
            <option value="__other__">Other (not listed)</option>
          </select>
          {(gasOtherSelected ||
            (!!pdiPrefs.gasPumpType &&
              !GAS_PUMP_TYPES.includes(pdiPrefs.gasPumpType))) && (
            <div className="mt-2">
              <TF
                id="pdi-gas-type-other"
                label="Enter pump type"
                value={pdiPrefs.gasPumpType ?? ""}
                onChange={(v) => updatePdi("gasPumpType", v)}
                maxLength={100}
                placeholder="e.g., a pump brand not listed above"
              />
            </div>
          )}
        </div>
      )}
      <TF
        id="pdi-wifi"
        label="Managed WiFi / switch interest"
        value={pdiPrefs.managedWifiInterest ?? ""}
        onChange={(v) => updatePdi("managedWifiInterest", v)}
        maxLength={200}
        placeholder="Leave blank for none"
      />
      <div>
        <label htmlFor="pdi-notes" className="label mb-1 block">
          Notes for PDI
        </label>
        <textarea
          id="pdi-notes"
          className="input min-h-[80px]"
          value={pdiPrefs.notes ?? ""}
          onChange={(e) => updatePdi("notes", e.target.value)}
          maxLength={1000}
          placeholder="Anything PDI's team should know about this order."
        />
      </div>
    </>
  );
}
